'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import type { TeaserFloorplanType } from '@/lib/types/teaser'

type Props = {
  data: TeaserFloorplanType
}

type FloorplanView = 'floor' | 'map'

const IFRAME_RE = /<iframe\b[\s\S]*?<\/iframe>/gi

function trimHtml(value: string | null | undefined): string {
  return value?.trim() ?? ''
}

function extractIframes(html: string): string[] {
  return html.match(IFRAME_RE) ?? []
}

/** True only when CMS HTML includes a real iframe embed (not empty richtext). */
function hasUsableEmbed(html: string | null | undefined): boolean {
  const value = trimHtml(html)
  if (!value) return false
  return /<iframe\b/i.test(value) && /\bsrc\s*=/i.test(value)
}

/**
 * CMS embeds often use protocol-relative URLs (`//host/...`). On localhost
 * (http) those become http iframes, which our CSP `frame-src https:` blocks.
 * Force https so embeds work in both local and production.
 */
function normalizeEmbedHtml(html: string): string {
  return html
    .replace(
      /(\b(?:src|href)\s*=\s*["'])\/\//gi,
      '$1https://',
    )
    .replace(
      /(\b(?:src|href)\s*=\s*)(\/\/)/gi,
      '$1https://',
    )
}

/**
 * Resolve Floor Plan / Map embeds from CMS:
 * 1) Explicit `floorplanScript` + `sightmapScript`
 * 2) Two iframes inside legacy `script` → floor, then map
 * 3) Single `script` → map when SightMap-like, else floor plan
 */
function resolveFloorplanViews(data: TeaserFloorplanType): {
  floorPlan: string | null
  map: string | null
} {
  const floorPlan =
    trimHtml(data.floorplanScript) || trimHtml(data.floorPlanScript) || null
  const map =
    trimHtml(data.sightmapScript) || trimHtml(data.mapScript) || null
  if (floorPlan || map) {
    return {
      floorPlan: floorPlan ? normalizeEmbedHtml(floorPlan) : null,
      map: map ? normalizeEmbedHtml(map) : null,
    }
  }

  const legacy = trimHtml(data.script)
  if (!legacy) return { floorPlan: null, map: null }

  const iframes = extractIframes(legacy)
  if (iframes.length >= 2) {
    return {
      floorPlan: normalizeEmbedHtml(iframes[0]),
      map: normalizeEmbedHtml(iframes[1]),
    }
  }

  const normalized = normalizeEmbedHtml(legacy)
  // Single embed — treat as map when it looks like SightMap, else floor plan.
  if (/sightmap/i.test(legacy)) {
    return { floorPlan: null, map: normalized }
  }
  return { floorPlan: normalized, map: null }
}

/**
 * `dangerouslySetInnerHTML` does not run `<script>` tags. Bishop Arts / similar
 * floorplan snippets need those scripts (host_domain + cross-domain helper).
 */
function mountEmbedHtml(container: HTMLElement, html: string) {
  container.replaceChildren()
  const template = document.createElement('template')
  template.innerHTML = html
  const nodes = Array.from(template.content.childNodes)

  for (const node of nodes) {
    if (node.nodeName !== 'SCRIPT') {
      container.appendChild(node.cloneNode(true))
      continue
    }

    const source = node as HTMLScriptElement
    const script = document.createElement('script')
    for (const { name, value } of Array.from(source.attributes)) {
      script.setAttribute(name, value)
    }
    if (source.textContent) {
      script.text = source.textContent
    }
    container.appendChild(script)
  }
}

function FloorplanEmbed({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    mountEmbedHtml(el, html)
    return () => {
      el.replaceChildren()
    }
  }, [html])

  return (
    <div
      ref={ref}
      className="w-full [&_iframe]:block [&_iframe]:h-[min(80vh,900px)] [&_iframe]:min-h-[480px] [&_iframe]:w-full [&_iframe]:border-0 lg:[&_iframe]:h-[calc(100dvh-var(--height-header)-11rem)] lg:[&_iframe]:min-h-[720px]"
    />
  )
}

function tabClass(active: boolean, disabled: boolean): string {
  // Solid fills — works on both photo heroes and compact linen heroes.
  const base =
    'min-h-11 flex-1 px-5 py-3 font-body text-xs font-bold uppercase tracking-[0.04em] text-white transition-colors md:px-6 md:text-sm lg:flex-none lg:min-w-[168px]'
  if (disabled) {
    return `${base} cursor-not-allowed bg-stone/60 text-white/70`
  }
  if (active) {
    return `${base} cursor-pointer bg-cta-primary`
  }
  return `${base} cursor-pointer bg-stone hover:bg-stone/90`
}

/** CMS `blocks.floorplan` — Floor Plan View / Map View toggle + embed. */
export default function TeaserFloorplan({ data }: Props) {
  const { floorPlan, map } = useMemo(() => resolveFloorplanViews(data), [data])
  // Enable a tab only when that view’s CMS script contains a real iframe.
  const hasFloor = hasUsableEmbed(floorPlan)
  const hasMap = hasUsableEmbed(map)
  const showToggle = hasFloor || hasMap

  const floorLabel = data.floorplanTitle?.trim() || 'Floor Plan View'
  const mapLabel = data.sightmapTitle?.trim() || 'Map View'

  const defaultView: FloorplanView = hasFloor ? 'floor' : 'map'
  const [view, setView] = useState<FloorplanView>(defaultView)

  // Never leave the UI on a missing CMS view.
  const activeView: FloorplanView =
    view === 'floor' && !hasFloor
      ? hasMap
        ? 'map'
        : 'floor'
      : view === 'map' && !hasMap
        ? hasFloor
          ? 'floor'
          : 'map'
        : view

  if (data.isVisible === false) return null

  const activeHtml =
    (activeView === 'map' && hasMap
      ? map
      : activeView === 'floor' && hasFloor
        ? floorPlan
        : null) ??
    (hasMap ? map : null) ??
    (hasFloor ? floorPlan : null) ??
    ''

  if (!activeHtml || !hasUsableEmbed(activeHtml)) return null

  return (
    <section
      aria-label={data.heading?.trim() || floorLabel || 'Floor plan'}
      data-fig="teaser-floorplan"
      className="relative z-20 w-full bg-surface-white px-4 md:px-0"
    >
      {/*
        --width-container is 1440px; without min(..., 100%-160px) viewports
        under 1440px stay full-bleed. Desktop always keeps ~80px side gutters.
      */}
      <div className="relative mx-auto w-full md:max-w-[min(var(--width-container),calc(100%-160px))]">
        {/*
          Toggle lives in the floorplan block but sits on the hero’s bottom-left
          edge (absolute + -translate-y-full), flush with the embed below.
          Missing CMS script/view → that tab stays disabled.
        */}
        {showToggle ? (
          <div
            role="tablist"
            aria-label="Floor plan views"
            className="absolute left-0 top-0 z-30 flex w-full -translate-y-full lg:w-auto"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeView === 'floor'}
              aria-disabled={!hasFloor}
              disabled={!hasFloor}
              title={
                hasFloor ? undefined : `${floorLabel} is not available`
              }
              id="floorplan-tab-floor"
              aria-controls="floorplan-panel"
              onClick={() => {
                if (!hasFloor) return
                setView('floor')
              }}
              className={tabClass(activeView === 'floor', !hasFloor)}
            >
              {floorLabel}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeView === 'map'}
              aria-disabled={!hasMap}
              disabled={!hasMap}
              title={hasMap ? undefined : `${mapLabel} is not available`}
              id="floorplan-tab-map"
              aria-controls="floorplan-panel"
              onClick={() => {
                if (!hasMap) return
                setView('map')
              }}
              className={tabClass(activeView === 'map', !hasMap)}
            >
              {mapLabel}
            </button>
          </div>
        ) : null}

        <div
          id="floorplan-panel"
          role={showToggle ? 'tabpanel' : undefined}
          aria-labelledby={
            showToggle
              ? activeView === 'map'
                ? 'floorplan-tab-map'
                : 'floorplan-tab-floor'
              : undefined
          }
        >
          {/* Key remounts embed when switching views so SightMap / scripts load cleanly. */}
          <FloorplanEmbed key={activeView} html={activeHtml} />
        </div>
      </div>
    </section>
  )
}
